import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Add as AddIcon,
  AccountTree as AccountTreeIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import api from '../services/api';
import { AppContext } from '../App';

const DEFAULT_BRANDING = {
  company_name: '',
  company_address: '',
  company_phone: '',
  company_email: '',
  accounts_email: '',
  vat_reg_number: '',
  director: '',
  company_colour: '#CF0202',
  logo_blob_path: '',
  logo_url: '',
  default_terms_and_conditions: '',
  require_project_when_clocking_out: false,
  send_clock_reminders: false,
  clock_in_reminder_time: '',
  clock_out_reminder_time: '',
  restrict_clock_location: false,
  clock_location_postcode: '',
  clock_location_radius_km: ''
};

const DEFAULT_REMINDER_TIMES = {
  clock_in_reminder_time: '07:00',
  clock_out_reminder_time: '17:00'
};

const normalizeReminderTime = (value) => {
  if (!value) {
    return '';
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toTimeString().slice(0, 5);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    const timeMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (timeMatch) {
      const hours = Number(timeMatch[1]);
      const minutes = Number(timeMatch[2]);

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }

    const parsedDate = new Date(`1970-01-01T${trimmedValue}`);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toTimeString().slice(0, 5);
    }
  }

  return '';
};

const CompanySettings = () => {
  const { currentUser } = useContext(AppContext) || {};
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  // Separate state for address fields
  const [addressFields, setAddressFields] = useState({
    line1: '',
    line2: '',
    city: '',
    postcode: ''
  });
  const [completeness, setCompleteness] = useState({
    total_fields: 0,
    filled_fields: 0,
    missing_fields: [],
    completeness_percentage: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [deleteLogoDialog, setDeleteLogoDialog] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [removeDepartmentDialog, setRemoveDepartmentDialog] = useState({
    open: false,
    department: '',
    index: null,
  });
  const [deleteCompanyDialog, setDeleteCompanyDialog] = useState(false);
  const [submittingDeletionRequest, setSubmittingDeletionRequest] = useState(false);

  useEffect(() => {
    fetchBrandingInfo();
  }, []);

  const fetchBrandingInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenant/branding');
      const normalizedBranding = {
        ...DEFAULT_BRANDING,
        ...response.data.branding,
        clock_in_reminder_time: normalizeReminderTime(response.data.branding?.clock_in_reminder_time),
        clock_out_reminder_time: normalizeReminderTime(response.data.branding?.clock_out_reminder_time),
        clock_location_radius_km: response.data.branding?.clock_location_radius_km ?? ''
      };
      setBranding(normalizedBranding);
      setCompleteness(response.data.completeness);
      setDepartments(normalizedBranding.departments || []);
      
      // Parse the comma-separated address into individual fields
      if (normalizedBranding.company_address) {
        const addressParts = normalizedBranding.company_address.split(',').map(part => part.trim());
        setAddressFields({
          line1: addressParts[0] || '',
          line2: addressParts[1] || '',
          city: addressParts[2] || '',
          postcode: addressParts[3] || ''
        });
      } else {
        setAddressFields({
          line1: '',
          line2: '',
          city: '',
          postcode: ''
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load company branding information'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBranding(prev => ({
      ...prev,
      [name]: name === 'clock_in_reminder_time' || name === 'clock_out_reminder_time'
        ? normalizeReminderTime(value)
        : value
    }));
  };

  const handleAddressFieldChange = (field) => (e) => {
    setAddressFields(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleColorChange = (e) => {
    setBranding(prev => ({
      ...prev,
      company_colour: e.target.value
    }));
  };

  const handleToggleChange = (e) => {
    const { name, checked } = e.target;
    setBranding(prev => {
      const nextBranding = {
        ...prev,
        [name]: checked
      };

      if (name === 'send_clock_reminders' && checked) {
        nextBranding.clock_in_reminder_time = normalizeReminderTime(prev.clock_in_reminder_time) || DEFAULT_REMINDER_TIMES.clock_in_reminder_time;
        nextBranding.clock_out_reminder_time = normalizeReminderTime(prev.clock_out_reminder_time) || DEFAULT_REMINDER_TIMES.clock_out_reminder_time;
      }

      return nextBranding;
    });
  };

  const handleReminderTimeChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: normalizeReminderTime(value)
    }));
  };

  const handleNativeTimeFieldInteract = (event) => {
    if (typeof event.target?.showPicker === 'function') {
      try {
        event.target.showPicker();
      } catch (error) {
        // Ignore browsers that block programmatic picker opening.
      }
    }
  };

  const handleAddDepartment = () => {
    const trimmed = newDepartment.trim();
    if (!trimmed) return;
    if (departments.some(d => d.toLowerCase() === trimmed.toLowerCase())) return;
    setDepartments(prev => [...prev, trimmed]);
    setNewDepartment('');
  };

  const handleRemoveDepartment = (index) => {
    setRemoveDepartmentDialog({
      open: true,
      department: departments[index] || '',
      index,
    });
  };

  const handleConfirmRemoveDepartment = () => {
    setDepartments(prev => prev.filter((_, i) => i !== removeDepartmentDialog.index));
    setRemoveDepartmentDialog({
      open: false,
      department: '',
      index: null,
    });
  };

  const handleCloseRemoveDepartmentDialog = () => {
    setRemoveDepartmentDialog({
      open: false,
      department: '',
      index: null,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Combine address fields into comma-separated string
      const addressString = [
        addressFields.line1,
        addressFields.line2,
        addressFields.city,
        addressFields.postcode
      ]
        .filter(part => part.trim()) // Remove empty parts
        .join(', ');

      const updatedBranding = {
        ...branding,
        company_address: addressString,
        departments: departments
      };

      updatedBranding.clock_in_reminder_time = normalizeReminderTime(updatedBranding.clock_in_reminder_time);
      updatedBranding.clock_out_reminder_time = normalizeReminderTime(updatedBranding.clock_out_reminder_time);

      setBranding(prev => ({
        ...prev,
        clock_in_reminder_time: updatedBranding.clock_in_reminder_time,
        clock_out_reminder_time: updatedBranding.clock_out_reminder_time
      }));

      if (updatedBranding.send_clock_reminders) {
        if (!updatedBranding.clock_in_reminder_time || !updatedBranding.clock_out_reminder_time) {
          setMessage({
            type: 'error',
            text: 'Clock in and clock out reminder times are required when reminders are enabled'
          });
          setSaving(false);
          return;
        }
      }

      if (updatedBranding.restrict_clock_location) {
        if (!String(updatedBranding.clock_location_postcode || '').trim()) {
          setMessage({
            type: 'error',
            text: 'A clock location postcode is required when location restriction is enabled'
          });
          setSaving(false);
          return;
        }

        const radius = Number(updatedBranding.clock_location_radius_km);
        if (!Number.isFinite(radius) || radius < 0.1) {
          setMessage({
            type: 'error',
            text: 'A clock location distance of at least 0.1 km is required when location restriction is enabled'
          });
          setSaving(false);
          return;
        }
      }

      const response = await api.put('/tenant/branding', updatedBranding);
      setBranding({
        ...DEFAULT_BRANDING,
        ...response.data.branding,
        clock_in_reminder_time: normalizeReminderTime(response.data.branding?.clock_in_reminder_time),
        clock_out_reminder_time: normalizeReminderTime(response.data.branding?.clock_out_reminder_time),
        clock_location_radius_km: response.data.branding?.clock_location_radius_km ?? ''
      });
      setCompleteness(response.data.completeness);
      setDepartments(response.data.branding.departments || []);
      
      setMessage({
        type: 'success',
        text: 'Updated successfully!'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update company branding'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    try {
      setUploadingLogo(true);
      setMessage({ type: '', text: '' });

      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await api.post('/tenant/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setBranding(prev => ({
        ...prev,
        logo_blob_path: response.data.logo_blob_path
      }));

      setLogoFile(null);
      setLogoPreview(null);
      
      setMessage({
        type: 'success',
        text: 'Logo uploaded successfully!'
      });

      // Refresh branding info to get updated completeness
      fetchBrandingInfo();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload logo'
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setMessage({ type: '', text: '' });

      await api.delete('/tenant/logo');

      setBranding(prev => ({
        ...prev,
        logo_blob_path: ''
      }));

      setDeleteLogoDialog(false);
      
      setMessage({
        type: 'success',
        text: 'Logo deleted successfully!'
      });

      // Refresh branding info to get updated completeness
      fetchBrandingInfo();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete logo'
      });
    }
  };

  const handleSubmitDeletionRequest = async () => {
    if (!currentUser?.email) {
      setMessage({
        type: 'error',
        text: 'Could not determine the email address for the logged in user'
      });
      return;
    }

    try {
      setSubmittingDeletionRequest(true);
      setMessage({ type: '', text: '' });

      await api.post('/contact', {
        email: currentUser.email,
        enquiry: 'tenant deletion request for ' + branding.company_name + ' authcode: ' + branding.company_colour
      });

      setDeleteCompanyDialog(false);
      setMessage({
        type: 'success',
        text: 'Tenant deletion request submitted successfully'
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to submit tenant deletion request'
      });
    } finally {
      setSubmittingDeletionRequest(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Company Settings
      </Typography>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage({ type: '', text: '' })}
        >
          {message.text}
        </Alert>
      )}

      {/* Completeness Overview */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Branding Setup Progress"
          avatar={<BusinessIcon color="primary" />}
        />
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={completeness.completeness_percentage}
              sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
            />
            <Typography variant="h6" color="primary">
              {completeness.completeness_percentage}%
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {completeness.filled_fields} of {completeness.total_fields} fields completed
          </Typography>
          {completeness.missing_fields.length > 0 && (
            <Box sx={{ mt: 1 }}>
              
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Company Information"
              avatar={<BusinessIcon color="primary" />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={branding.company_name}
                    disabled
                    variant="outlined"
                    helperText="Company name cannot be changed from here"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={addressFields.line1}
                    onChange={handleAddressFieldChange('line1')}
                    variant="outlined"
                    placeholder="Street address"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={addressFields.line2}
                    onChange={handleAddressFieldChange('line2')}
                    variant="outlined"
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={addressFields.city}
                    onChange={handleAddressFieldChange('city')}
                    variant="outlined"
                    placeholder="City"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Postcode"
                    value={addressFields.postcode}
                    onChange={handleAddressFieldChange('postcode')}
                    variant="outlined"
                    placeholder="Postcode"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="company_phone"
                    value={branding.company_phone || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="company_email"
                    type="email"
                    value={branding.company_email || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="info@yourcompany.com"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Accounts Email Address"
                    name="accounts_email"
                    type="email"
                    value={branding.accounts_email || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="accounts@yourcompany.com"
                    helperText="Receives a confirmation with the request details whenever any time-off request is approved."
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="VAT Registration Number"
                    name="vat_reg_number"
                    value={branding.vat_reg_number || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="123 4567 89"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>
                    Company Theme Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="color"
                      value={branding.company_colour || '#CF0202'}
                      onChange={handleColorChange}
                      style={{
                        width: 50,
                        height: 40,
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer'
                      }}
                    />
                    <TextField
                      label="Color Code"
                      value={branding.company_colour || '#CF0202'}
                      onChange={handleColorChange}
                      variant="outlined"
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Logo Management */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Company Logo"
              avatar={<PaletteIcon color="primary" />}
            />
            <CardContent>
              {branding.logo_blob_path ? (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Logo
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 120,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      backgroundColor: 'background.paper',
                      p: 1
                    }}
                  >
                    {branding.logo_url ? (
                      <img
                        src={branding.logo_url}
                        alt="Company Logo"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: 4
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentNode.innerHTML = `<Typography variant="body2" color="text.secondary">Logo: ${branding.logo_blob_path.split('/').pop()}</Typography>`;
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Logo: {branding.logo_blob_path.split('/').pop()}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteLogoDialog(true)}
                    fullWidth
                  >
                    Delete Logo
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No logo uploaded
                </Typography>
              )}

              {logoPreview && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Preview
                  </Typography>
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 120,
                      borderRadius: 4
                    }}
                  />
                </Box>
              )}

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoFileSelect}
              />
              <label htmlFor="logo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Choose Logo
                </Button>
              </label>

              {logoFile && (
                <Button
                  variant="contained"
                  onClick={handleLogoUpload}
                  disabled={uploadingLogo}
                  startIcon={<SaveIcon />}
                  fullWidth
                >
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Time Tracking Rules"
              subheader="Control team member clock-out requirements and reminders"
              avatar={<WarningAmberIcon color="primary" />}
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(branding.require_project_when_clocking_out)}
                    onChange={handleToggleChange}
                    name="require_project_when_clocking_out"
                  />
                }
                label="Require project when clocking out"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                When enabled, team members must choose one of their assigned projects before they can clock out.
              </Typography>

              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(branding.restrict_clock_location)}
                      onChange={handleToggleChange}
                      name="restrict_clock_location"
                    />
                  }
                  label="Restrict clock in/out location"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  When enabled, team members must be within the configured distance of the configured postcode to clock in or out.
                </Typography>

                {branding.restrict_clock_location && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Clock Location Postcode"
                        name="clock_location_postcode"
                        value={branding.clock_location_postcode || ''}
                        onChange={handleInputChange}
                        helperText="Enter the postcode to use for clock location checks"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Allowed Distance (km)"
                        name="clock_location_radius_km"
                        type="number"
                        value={branding.clock_location_radius_km ?? ''}
                        onChange={handleInputChange}
                        inputProps={{ min: 0.1, step: 0.1 }}
                        helperText="Team members must be within this many kilometres"
                        required
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(branding.send_clock_reminders)}
                      onChange={handleToggleChange}
                      name="send_clock_reminders"
                    />
                  }
                  label="Send clock in/out reminders"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Sends weekday Expo mobile push reminders for clocking in and clocking out. These reminders are not added to the in-app notifications list.
                </Typography>

                {branding.send_clock_reminders && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Clock In Reminder Time"
                        name="clock_in_reminder_time"
                        type="time"
                        value={branding.clock_in_reminder_time || ''}
                        onChange={(event) => handleReminderTimeChange('clock_in_reminder_time', event.target.value)}
                        onClick={handleNativeTimeFieldInteract}
                        onFocus={handleNativeTimeFieldInteract}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        helperText="Weekdays only."
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Clock Out Reminder Time"
                        name="clock_out_reminder_time"
                        type="time"
                        value={branding.clock_out_reminder_time || ''}
                        onChange={(event) => handleReminderTimeChange('clock_out_reminder_time', event.target.value)}
                        onClick={handleNativeTimeFieldInteract}
                        onFocus={handleNativeTimeFieldInteract}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 60 }}
                        helperText="Weekdays only."
                        required
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Terms & Conditions */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Default Terms & Conditions"
              avatar={<DescriptionIcon color="primary" />}
            />
            <CardContent>
              <TextField
                fullWidth
                label="Terms & Conditions"
                name="default_terms_and_conditions"
                multiline
                rows={8}
                value={branding.default_terms_and_conditions || ''}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Enter your default terms and conditions that will appear on quotes..."
                helperText="These terms will be automatically included on all quotes unless overridden"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Departments */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Departments"
              subheader="Configure department names used to organize projects and purchase orders"
              avatar={<AccountTreeIcon color="primary" />}
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="New Department"
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDepartment(); } }}
                  variant="outlined"
                  size="small"
                  placeholder="e.g. Engineering"
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddDepartment}
                  disabled={!newDepartment.trim()}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>

              {departments.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {departments.map((dept, index) => (
                    <Chip
                      key={index}
                      label={dept}
                      onDelete={() => handleRemoveDepartment(index)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No departments configured. The default 'General' department will be used.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={<SaveIcon />}
          sx={{
            minWidth: 200,
            py: 1.5,
            borderRadius: 2,
            fontSize: '1.1rem'
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Card
        sx={{
          mt: 4,
          border: '1px solid',
          borderColor: 'error.main',
          backgroundColor: 'rgba(211, 47, 47, 0.04)'
        }}
      >
        <CardHeader
          title="Danger Zone"
          subheader="Request permanent deletion of this company account from Struxor. This will remove all data and user accounts associated with this company."
          avatar={<WarningAmberIcon color="error" />}
          sx={{
            '& .MuiCardHeader-title': {
              color: 'error.main',
              fontWeight: 600
            }
          }}
        />
        <CardContent>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteCompanyDialog(true)}
            disabled={!currentUser?.email || submittingDeletionRequest}
          >
            Delete Company Account From Struxor
          </Button>
        </CardContent>
      </Card>

      {/* Delete Logo Confirmation Dialog */}
      <Dialog open={deleteLogoDialog} onClose={() => setDeleteLogoDialog(false)}>
        <DialogTitle>Delete Company Logo</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the company logo? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLogoDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteLogo} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeDepartmentDialog.open} onClose={handleCloseRemoveDepartmentDialog}>
        <DialogTitle>Remove Department</DialogTitle>
        <DialogContent>
          <Typography>
            Removing {removeDepartmentDialog.department ? `"${removeDepartmentDialog.department}"` : 'this department'} cannot be undone. Everything currently assigned to this department will be set to General, including users, projects, and purchase orders.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDepartmentDialog}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRemoveDepartment} color="error" variant="contained">
            Remove Department
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteCompanyDialog}
        onClose={() => {
          if (!submittingDeletionRequest) {
            setDeleteCompanyDialog(false);
          }
        }}
      >
        <DialogTitle>Request Company Account Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            This will send a tenant deletion request for this company account using {currentUser?.email || 'your logged in email address'}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteCompanyDialog(false)}
            disabled={submittingDeletionRequest}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitDeletionRequest}
            color="error"
            variant="contained"
            disabled={submittingDeletionRequest || !currentUser?.email}
          >
            {submittingDeletionRequest ? 'Submitting...' : 'Send Deletion Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CompanySettings;
